import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  LayoutDashboard,
  FolderKanban,
  Wallet,
  FileText,
  MessageSquare,
  Users,
  LogOut,
  ChevronDown,
  User,
  FileSignature,
  UserPlus,
  HandCoins,
  Image,
  BookOpen,
  Rocket,
  Calendar,
  Receipt,
  Euro,
  Key,
  Clock,
  Settings,
  Code,
  Shield,
  Globe
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

// =============================================
// SIDEBAR V1 - ESPACE CLIENT MSC (Simplifié)
// =============================================
// Navigation uniquement pour la V1
// Accès ADMIN et DEVELOP sont des pages séparées
// =============================================

const sectionColors = {
  production: { bg: 'bg-emerald-500/10', text: 'text-emerald-300', accent: '#10b981' },
  commerce: { bg: 'bg-sky-500/10', text: 'text-sky-300', accent: '#0ea5e9' },
  finance: { bg: 'bg-amber-500/10', text: 'text-amber-300', accent: '#f59e0b' },
  partage: { bg: 'bg-pink-500/10', text: 'text-pink-300', accent: '#ec4899' },
  support: { bg: 'bg-violet-500/10', text: 'text-violet-300', accent: '#8b5cf6' },
  projects: { bg: 'bg-emerald-500/10', text: 'text-emerald-300', accent: '#10b981' },
};

const Sidebar = ({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) => {
  const { user, logout, isAgency, isTeamMember, isClient, isCustomerService, isAffiliate } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [expandedSections, setExpandedSections] = useState({
    production: true,
    commerce: true,
    finance: true,
    partage: true,
    support: true,
    projects: true
  });

  const [copiloteEnabled, setCopiloteEnabled] = useState(false);

  // Force re-render when user role changes
  const [, forceUpdate] = useState({});
  useEffect(() => {
    // This ensures the sidebar re-renders when user data changes
    forceUpdate({});
  }, [user, isAgency, isTeamMember, isClient, isCustomerService, isAffiliate]);

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${API}/api/cms/site-settings`)
      .then(r => r.json())
      .then(data => setCopiloteEnabled(data?.copilote_section?.enabled === true))
      .catch(() => {});
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // =============================================
  // NAVIGATION AGENCE V1 - Espace Client uniquement
  // =============================================
  const agencyNavigation = [
    { to: '/dashboard/agency', icon: LayoutDashboard, label: 'Tableau de bord' },
    
    {
      section: 'production',
      label: 'Production',
      icon: FolderKanban,
      items: [
        { to: '/projects', icon: FolderKanban, label: 'Projets' },
        { to: '/planning', icon: Calendar, label: 'Planning' },
        { to: '/tickets', icon: MessageSquare, label: 'Support' },
        { to: '/contracts', icon: FileSignature, label: 'Contrats' },
      ]
    },
    
    {
      section: 'commerce',
      label: 'Commerce',
      icon: Euro,
      items: [
        { to: '/prospects', icon: UserPlus, label: 'Prospects' },
        { to: '/clients', icon: Users, label: 'Clients' },
        { to: '/project-requests', icon: FileText, label: 'Demandes' },
        { to: '/affiliates', icon: HandCoins, label: 'Apporteurs' },
      ]
    },
    
    {
      section: 'finance',
      label: 'Finance',
      icon: Receipt,
      items: [
        { to: '/receipts/pending', icon: Receipt, label: 'Reçus' },
        { to: '/payment-requests', icon: Euro, label: 'Paiements' },
        { to: '/reports', icon: FileText, label: 'Rapports' },
      ]
    },
    
    {
      section: 'partage',
      label: 'Espaces Partagés',
      icon: Key,
      items: [
        { to: '/shared-supports', icon: Image, label: 'Supports' },
        { to: '/shared-passwords', icon: Key, label: 'Accès' },
      ]
    },
  ];

  // Navigation Client V1
  const clientNavigation = [
    { to: '/dashboard/client', icon: LayoutDashboard, label: 'Mon espace' },
    
    {
      section: 'projects',
      label: 'Mes Projets',
      icon: FolderKanban,
      items: [
        { to: '/new-project-request', icon: FileText, label: 'Nouvelle demande' },
        { to: '/my-projects', icon: FolderKanban, label: 'Projets en cours' },
        { to: '/my-contracts', icon: FileSignature, label: 'Contrats' },
      ]
    },
    
    {
      section: 'finance',
      label: 'Mon Compte',
      icon: Wallet,
      items: [
        { to: '/wallet', icon: Wallet, label: 'Wallet' },
        { to: '/my-receipts', icon: Receipt, label: 'Reçus & Factures' },
      ]
    },
    
    {
      section: 'partage',
      label: 'Espace Partagé',
      icon: Image,
      items: [
        { to: '/shared-supports', icon: Image, label: 'Supports' },
        { to: '/shared-passwords', icon: Key, label: 'Accès' },
      ]
    },
    
    {
      section: 'support',
      label: 'Assistance',
      icon: MessageSquare,
      items: [
        { to: '/tickets', icon: MessageSquare, label: 'Support' },
        { to: '/knowledge', icon: BookOpen, label: 'Aide' },
      ]
    },
  ];

  // Navigation Créatif V1
  const creativeNavigation = [
    { to: '/dashboard/team', icon: LayoutDashboard, label: 'Mon espace' },
    { to: '/my-projects', icon: FolderKanban, label: 'Mes projets' },
    { to: '/planning', icon: Calendar, label: 'Planning' },
    { to: '/payment-requests', icon: Euro, label: 'Paiements' },
    { to: '/profile', icon: User, label: 'Profil' },
  ];

  // Navigation Service Client V1
  const customerServiceNavigation = [
    { to: '/dashboard/customer-service', icon: LayoutDashboard, label: 'Tableau de bord' },
    { to: '/clients', icon: Users, label: 'Clients' },
    { to: '/tickets', icon: MessageSquare, label: 'Tickets' },
    { to: '/receipts/pending', icon: Receipt, label: 'Reçus' },
  ];

  // Navigation Apporteur V1
  const affiliateNavigation = [
    { to: '/affiliate/dashboard', icon: LayoutDashboard, label: 'Tableau de bord' },
    { to: '/affiliate/referrals', icon: Users, label: 'Filleuls' },
    { to: '/affiliate/commissions', icon: Euro, label: 'Commissions' },
    { to: '/profile', icon: User, label: 'Profil' },
  ];

  // Sélection de la navigation selon le rôle
  const getNavigation = () => {
    if (isClient) return clientNavigation;
    if (isTeamMember) return creativeNavigation;
    if (isCustomerService) return customerServiceNavigation;
    if (isAffiliate) return affiliateNavigation;
    return agencyNavigation;
  };

  const navigation = getNavigation();

  const renderNavItem = (item, sectionKey = null) => {
    const Icon = item.icon;
    const colors = sectionKey ? sectionColors[sectionKey] : null;
    
    return (
      <NavLink
        key={item.to}
        to={item.to}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm",
            collapsed ? "justify-center" : "",
            isActive
              ? colors 
                ? `${colors.bg} ${colors.text} font-medium`
                : "bg-white/10 text-white font-medium"
              : "text-white/60 hover:text-white hover:bg-white/5"
          )
        }
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        {!collapsed && <span>{item.label}</span>}
      </NavLink>
    );
  };

  const renderSection = (section) => {
    const Icon = section.icon;
    const colors = sectionColors[section.section];
    const isExpanded = expandedSections[section.section];
    
    return (
      <div key={section.section} className="mb-2">
        <button
          onClick={() => toggleSection(section.section)}
          className={cn(
            "w-full flex items-center justify-between px-3 py-2 rounded-lg transition-all",
            "text-white/70 hover:text-white hover:bg-white/5",
            collapsed && "justify-center"
          )}
        >
          <div className="flex items-center gap-3">
            <Icon className="w-4 h-4" style={{ color: colors?.accent }} />
            {!collapsed && <span className="text-sm font-medium">{section.label}</span>}
          </div>
          {!collapsed && (
            <ChevronDown className={cn(
              "w-4 h-4 transition-transform",
              isExpanded && "rotate-180"
            )} />
          )}
        </button>
        
        {!collapsed && isExpanded && (
          <div className="ml-4 mt-1 space-y-1 border-l border-white/10 pl-3">
            {section.items.map(item => renderNavItem(item, section.section))}
          </div>
        )}
      </div>
    );
  };

  const sidebarContent = (
    <>
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <div className={cn("flex items-center gap-3", collapsed && "justify-center")}>
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: theme.colors?.primary || '#1a365d' }}
          >
            {theme.logo?.darkUrl ? (
              <img src={theme.logo.darkUrl} alt="Logo" className="w-8 h-8 object-contain" />
            ) : (
              <div className="flex items-baseline gap-0.5">
                <span className="font-black text-sm text-white tracking-tight">MSC</span>
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: theme.colors?.accent || '#00d4aa' }} />
              </div>
            )}
          </div>
          {!collapsed && (
            <div>
              <h1 className="text-white font-bold text-lg">
                {isClient ? 'Espace Client' : 'Espace Client'}
              </h1>
              <p className="text-white/50 text-xs">{theme.agency?.name || 'MSC L\'AGENCE'}</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 overflow-y-auto">
        <div className="space-y-1">
          {navigation.map((item) => (
            item.section ? renderSection(item) : renderNavItem(item)
          ))}
        </div>

        {/* Copilote Link for Clients - Only when enabled */}
        {isClient && !collapsed && copiloteEnabled && (
          <div className="mt-4 px-3">
            <NavLink
              to="/copilote/coming-soon"
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-orange-400/70 hover:text-orange-300 hover:bg-orange-500/10 transition-all text-sm"
            >
              <Rocket className="w-3.5 h-3.5" />
              <span>Copilote</span>
              <span className="text-[10px] px-1 py-0.5 bg-orange-500/20 rounded text-orange-400">Bientôt</span>
            </NavLink>
          </div>
        )}

        {/* Quick Access for Agency - Separate zones */}
        {isAgency && !collapsed && (
          <div className="mt-6 pt-4 border-t border-white/10">
            <p className="px-3 text-xs text-white/40 uppercase tracking-wider mb-2">Autres accès</p>
            <div className="space-y-2 px-2">
              <button
                onClick={() => navigate('/admin')}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-amber-300/70 hover:text-amber-300 hover:bg-amber-500/10 transition-all text-sm"
              >
                <Shield className="w-4 h-4" />
                <span>ADMIN</span>
              </button>
              <button
                onClick={() => navigate('/develop')}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-violet-300/70 hover:text-violet-300 hover:bg-violet-500/10 transition-all text-sm"
              >
                <Code className="w-4 h-4" />
                <span>DEVELOP</span>
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-white/10">
        <div className={cn("flex items-center", collapsed ? "justify-center" : "gap-3")}>
          <div 
            className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-sm"
            style={{ backgroundColor: theme.colors?.accent || '#00d4aa' }}
          >
            {user?.first_name?.[0] || user?.username?.[0] || 'U'}
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">
                  {user?.first_name || user?.username}
                </p>
                <p className="text-white/50 text-xs truncate">{user?.email}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                className="text-white/50 hover:text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 h-full z-40 transition-all duration-300 hidden md:flex flex-col",
          collapsed ? "w-20" : "w-64"
        )}
        style={{ backgroundColor: theme.colors?.primary || '#1a365d' }}
      >
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <aside
            className="fixed top-0 left-0 h-full w-64 z-50 flex flex-col md:hidden"
            style={{ backgroundColor: theme.colors?.primary || '#1a365d' }}
          >
            {sidebarContent}
          </aside>
        </>
      )}
    </>
  );
};

export default Sidebar;
